import {
  openServiceCallRequestSchema,
  SERVICE_MESSAGE_MAX,
  SERVICE_SUBJECT_MAX,
  SERVICE_TICKET_KINDS,
  type ServiceTicket,
  type ServiceTicketDetail,
  type ServiceTicketKind,
  type ServiceTicketStatus,
} from '@avrahami/contracts';
import {
  Alert,
  Button,
  Card,
  formatDateTime,
  Loading,
  NO_ERRORS,
  PageHeader,
  SelectField,
  ServiceThread,
  StatusBadge,
  TextAreaField,
  TextField,
  type FieldErrors,
} from '@avrahami/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useState, type SyntheticEvent } from 'react';
import {
  keys,
  serviceCallQuery,
  serviceCallsQuery,
  tenantApi,
  useStoreTimeZone,
} from '../api/tenant';
import { he } from '../i18n/he';
import { errorMessage, mergeErrors, parseWith } from '../lib/forms';

const STATUS_TONES = {
  OPEN: 'amber',
  IN_PROGRESS: 'blue',
  RESOLVED: 'green',
} as const satisfies Record<ServiceTicketStatus, 'amber' | 'blue' | 'green'>;

/**
 * The store's service calls to the platform (adr/0036): a call is opened with a subject and the
 * details, and the conversation with the platform's staff goes on under it.
 */
export function SupportPage() {
  const search = useSearch({ from: '/app/support' });
  const navigate = useNavigate({ from: '/support' });
  const calls = useQuery(serviceCallsQuery());
  const timeZone = useStoreTimeZone();
  const [writing, setWriting] = useState(false);
  const choose = (ticketId: string | undefined) =>
    void navigate({ search: { ticket: ticketId }, replace: true });

  return (
    <>
      <PageHeader
        title={he.support.title}
        actions={
          writing ? null : (
            <Button
              data-testid="support-new"
              onClick={() => {
                setWriting(true);
              }}
            >
              {he.support.newCall}
            </Button>
          )
        }
      />
      <p className="mb-4 max-w-3xl text-sm text-slate-600">{he.support.intro}</p>
      {writing ? (
        <div className="mb-6">
          <NewCallForm
            onDone={(opened) => {
              setWriting(false);
              if (opened !== undefined) choose(opened);
            }}
          />
        </div>
      ) : null}
      {calls.isPending ? <Loading /> : null}
      {calls.isError ? <Alert>{errorMessage(calls.error)}</Alert> : null}
      {calls.data === undefined ? null : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
          <Card title={he.support.list}>
            {calls.data.tickets.length === 0 ? (
              <p className="text-sm text-slate-500" data-testid="support-empty">
                {he.support.empty}
              </p>
            ) : (
              <ul className="flex flex-col divide-y divide-slate-100" data-testid="support-list">
                {calls.data.tickets.map((ticket) => (
                  <CallRow
                    key={ticket.id}
                    ticket={ticket}
                    timeZone={timeZone}
                    selected={ticket.id === search.ticket}
                    onSelect={() => {
                      choose(ticket.id);
                    }}
                  />
                ))}
              </ul>
            )}
          </Card>
          {search.ticket === undefined ? (
            <Card>
              <p className="text-sm text-slate-600">{he.support.pick}</p>
            </Card>
          ) : (
            <CallPanel key={search.ticket} ticketId={search.ticket} timeZone={timeZone} />
          )}
        </div>
      )}
    </>
  );
}

function CallRow({
  ticket,
  selected,
  timeZone,
  onSelect,
}: {
  ticket: ServiceTicket;
  selected: boolean;
  timeZone: string;
  onSelect: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-current={selected ? 'true' : undefined}
        data-testid="support-row"
        className={`flex w-full flex-col items-start gap-1 rounded-md px-2 py-3 text-start text-sm hover:bg-slate-50 ${
          selected ? 'bg-brand-50 ring-1 ring-brand-200' : ''
        }`}
      >
        <span className="font-medium text-slate-800">{ticket.subject}</span>
        <span className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <StatusBadge value={ticket.status} labels={he.support.statuses} tones={STATUS_TONES} />
          {he.support.kinds[ticket.kind]} · {he.support.messages(ticket.messageCount)} ·{' '}
          {formatDateTime(ticket.updatedAt, timeZone)}
        </span>
      </button>
    </li>
  );
}

function CallPanel({ ticketId, timeZone }: { ticketId: string; timeZone: string }) {
  const queryClient = useQueryClient();
  const detail = useQuery(serviceCallQuery(ticketId));
  const refresh = async (next: ServiceTicketDetail) => {
    queryClient.setQueryData(keys.serviceTicket(ticketId), next);
    await queryClient.invalidateQueries({ queryKey: keys.service(), exact: true });
  };
  if (detail.isPending) {
    return (
      <Card>
        <Loading />
      </Card>
    );
  }
  if (detail.isError) {
    return (
      <Card>
        <Alert>{errorMessage(detail.error, { NOT_FOUND: he.support.notFound })}</Alert>
      </Card>
    );
  }
  const { ticket } = detail.data;
  return (
    <Card title={ticket.subject ?? ''}>
      <div className="flex flex-col gap-4" data-testid="support-call">
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <StatusBadge value={ticket.status} labels={he.support.statuses} tones={STATUS_TONES} />
          <span>{he.support.kinds[ticket.kind]}</span>
          <span>
            {he.support.opened}: {formatDateTime(ticket.createdAt, timeZone)}
          </span>
        </div>
        {ticket.status === 'RESOLVED' ? <Alert tone="info">{he.support.resolvedNote}</Alert> : null}
        <ServiceThread
          detail={detail.data}
          viewer="STORE"
          words={he.support.reply}
          canReply
          onReply={async (body) => {
            await refresh(await tenantApi.replyServiceCall(ticketId, body));
          }}
          errorMessage={errorMessage}
          timeZone={timeZone}
        />
      </div>
    </Card>
  );
}

function NewCallForm({ onDone }: { onDone: (openedId: string | undefined) => void }) {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<ServiceTicketKind>('GENERAL');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [localErrors, setLocalErrors] = useState<FieldErrors>(NO_ERRORS);
  const open = useMutation({
    mutationFn: tenantApi.openServiceCall,
    onSuccess: async (opened) => {
      queryClient.setQueryData(keys.serviceTicket(opened.ticket.id), opened);
      await queryClient.invalidateQueries({ queryKey: keys.service(), exact: true });
      onDone(opened.ticket.id);
    },
  });
  const errors = mergeErrors(localErrors, open.error);
  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    const parsed = parseWith(openServiceCallRequestSchema, { kind, subject, body });
    if (!parsed.ok) {
      setLocalErrors(parsed.errors);
      return;
    }
    setLocalErrors(NO_ERRORS);
    open.mutate(parsed.value);
  };
  return (
    <Card title={he.support.newCall}>
      <form className="flex max-w-2xl flex-col gap-4" onSubmit={submit} noValidate>
        <SelectField
          label={he.support.kind}
          name="kind"
          value={kind}
          hint={kind === 'SECURITY' ? he.support.securityHint : undefined}
          onChange={(event) => {
            const next = SERVICE_TICKET_KINDS.find((each) => each === event.target.value);
            if (next !== undefined) setKind(next);
          }}
        >
          {SERVICE_TICKET_KINDS.map((each) => (
            <option key={each} value={each}>
              {he.support.kinds[each]}
            </option>
          ))}
        </SelectField>
        <TextField
          label={he.support.subject}
          name="subject"
          hint={he.support.subjectHint}
          maxLength={SERVICE_SUBJECT_MAX}
          value={subject}
          error={errors['subject']}
          onChange={(event) => {
            setSubject(event.target.value);
          }}
        />
        <TextAreaField
          label={he.support.body}
          name="body"
          rows={6}
          hint={he.support.bodyHint}
          maxLength={SERVICE_MESSAGE_MAX}
          value={body}
          error={errors['body']}
          onChange={(event) => {
            setBody(event.target.value);
          }}
        />
        {open.isError && Object.keys(errors).length === 0 ? (
          <Alert>{errorMessage(open.error)}</Alert>
        ) : null}
        <div className="flex gap-2">
          <Button type="submit" busy={open.isPending} data-testid="support-send">
            {he.support.send}
          </Button>
          <Button
            tone="secondary"
            onClick={() => {
              onDone(undefined);
            }}
          >
            {he.support.cancel}
          </Button>
        </div>
      </form>
    </Card>
  );
}
